import { Cesium } from 'app/cesium';
import { pointAsArrayToObject } from 'app/form_config/components/svg_utils';
import { LonLatValueType, PolygonValueType } from 'app/form_config/helpers';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { LocationStatCollection } from 'app/shared/collections/location_stat_collection.service';
import {
    AnyFieldSchemaType,
    FIELD_SCHEMAS__TYPE_ASSET,
    FIELD_SCHEMAS__TYPE_POINT,
    FIELD_SCHEMAS__TYPE_POLYGON,
} from 'app/shared/collections/StatDefinition';
import { ImsTimeCollection } from 'app/shared/collections/time/ims_time_collection.factory';
import { ViewPropagateLocationSchema } from 'app/shared/collections/view';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { MapOptionsService } from 'app/shared/map_options/MapOptionsService';
import { cartographicPolygonCentroid } from 'app/shared/utilities/mapping_utils';
import { isNotNil } from 'app/shared/utils';
import { BaseViewController } from 'app/views/BaseViewController';
import { getIndicatorMapOptionName } from 'app/views/view_types/view_utils';

export type AssetLocationProviderFn = (assetId: number, time: number) => Cesium.Cartesian3;

function isMapOptionEnabled(
    config: ViewPropagateLocationSchema,
    assetOptionsService: MapOptionsService,
): boolean {
    const mapOptionName = getIndicatorMapOptionName(config);
    return assetOptionsService.selectedMapOptions[mapOptionName] as any;
}

function getFieldTypeAndCollection(
    imsItemTypRegistry: ImsItemTypeRegistry,
    table: string,
    field: string,
): [collection: ImsTimeCollection, fieldType: AnyFieldSchemaType] {
    const relationshipTypeDetails = imsItemTypRegistry.getTypeDetails(table);
    const collection: ImsTimeCollection = relationshipTypeDetails
        ? relationshipTypeDetails.collection
        : null;
    const fieldType =
        relationshipTypeDetails &&
        relationshipTypeDetails?.statDefinition?.field_schemas &&
        relationshipTypeDetails.statDefinition.field_schemas[field]
            ? relationshipTypeDetails.statDefinition.field_schemas[field].type
            : null;
    return [collection, fieldType];
}

function isInvalidType(value, acceptableTypes) {
    return value && !acceptableTypes.includes(value);
}

type CachedLocationPropagationLookup = {
    relationshipCollection: ImsTimeCollection;
    relationshipField: string;

    srcPosCollection: ImsTimeCollection;
    srcPosFieldType: AnyFieldSchemaType;
    srcPosField: string;
};

function createLocationPropagationLookupCache(
    view: BaseViewController,
    assetOptionsService: MapOptionsService,
    imsItemTypRegistry: ImsItemTypeRegistry,
): Array<CachedLocationPropagationLookup> {
    return (
        (view?._imsView?.locationPropagations || [])
            .filter((c) => isMapOptionEnabled(c, assetOptionsService))
            .map((config) => {
                if (!config.relationship_table && !config.src_position_table) {
                    console.error(
                        `${config.type} config error for: ${config.display_name}; neither relationship table or source location table is provided`,
                    );
                    return null;
                }

                const [relationshipCollection, relationshipFieldType] = getFieldTypeAndCollection(
                    imsItemTypRegistry,
                    config.relationship_table,
                    config.relationship_field,
                );
                if (isInvalidType(relationshipFieldType, [FIELD_SCHEMAS__TYPE_ASSET])) {
                    console.error(
                        `${config.type} config error for: ${config.display_name}; invalid relationship field type: ${relationshipFieldType}`,
                    );
                    return null;
                }

                const [sourceCollection, sourceFieldType] = getFieldTypeAndCollection(
                    imsItemTypRegistry,
                    config.src_position_table,
                    config.src_position_field,
                );
                if (
                    isInvalidType(sourceFieldType, [
                        FIELD_SCHEMAS__TYPE_POLYGON,
                        FIELD_SCHEMAS__TYPE_POINT,
                    ])
                ) {
                    console.error(
                        `${config.type} config error for: ${config.display_name}; invalid source position field type: ${sourceFieldType}`,
                    );
                    return null;
                }
                return {
                    relationshipCollection,
                    relationshipField: config.relationship_field,
                    srcPosCollection: sourceCollection,
                    srcPosField: config.src_position_field,
                    srcPosFieldType: sourceFieldType,
                };
            })
            // remove any invalid configs
            .filter(isNotNil)
    );
}

function getStatValue<T = any>(
    collection: ImsTimeCollection,
    assetId: number,
    field: string,
    time: number,
): T {
    const relationshipStats = collection.getStatsUnix(assetId, time);
    const relationShipStat = relationshipStats.length ? relationshipStats[0] : null;
    return relationShipStat ? relationShipStat[field] : null;
}

function polygonToCartesian(polygon: PolygonValueType) {
    const location = cartographicPolygonCentroid(polygon.map(pointAsArrayToObject));
    return Cesium.Cartesian3.fromDegrees(location.longitude, location.latitude, 0);
}

function getAssetPosition(
    locationStatCollection: LocationStatCollection,
    assetId: number,
    time: number,
): Cesium.Cartesian3 {
    return locationStatCollection.getPositionAtUnixTime(assetId, time, false);
}

function isPolygonValueType(
    value: PolygonValueType | LonLatValueType,
    type: string,
): value is PolygonValueType {
    return type === FIELD_SCHEMAS__TYPE_POLYGON;
}

function convertPositionToCartesian3(
    value: PolygonValueType | LonLatValueType,
    fieldType: AnyFieldSchemaType,
) {
    if (isPolygonValueType(value, fieldType)) {
        // if we've got a polygon as a value, just find the centroid of that (no interpolation)
        return polygonToCartesian(value);
    } else if (fieldType === FIELD_SCHEMAS__TYPE_POINT) {
        // if we've got a point as a value, just turn it from cartographic to cartesian
        return Cesium.Cartesian3.fromDegrees(value[0], value[1], 0);
    }
}

/**
 * The caller is responsible for recreating this function
 *  whenever any of the parameters change value internally.
 *  Ie, this function will not subscribe itself to change events of the passed
 *  in instances.
 */
export function createLocationProviderFn(
    locationStatCollection: LocationStatCollection,
    imsItemTypRegistry: ImsItemTypeRegistry,
    assetCollection: AssetCollection,
    view: BaseViewController,
    assetOptionsService: MapOptionsService,
): AssetLocationProviderFn {
    const locationPropagations = createLocationPropagationLookupCache(
        view,
        assetOptionsService,
        imsItemTypRegistry,
    );

    // fast default path
    if (!locationPropagations.length) {
        return (assetId, time) => getAssetPosition(locationStatCollection, assetId, time);
    }

    // slow path, iterate each potential location propagation, finding one that returns a result
    return (assetId: number, time: number) => {
        // we'll use the first location propagation that returns a location,
        //  otherwise default to its own location
        for (const prop of locationPropagations) {
            let targetAssetId = null;
            if (prop.relationshipCollection) {
                // follow the relationship if there is one
                const relationshipForeignKey = getStatValue(
                    prop.relationshipCollection,
                    assetId,
                    prop.relationshipField,
                    time,
                );
                // if we were meant to have a relationship to follow
                //  but didn't at this time, skip this propagation config
                if (!relationshipForeignKey) {
                    continue;
                }
                // we've successfully followed a relationship, and no source location target is provided,
                //  which means use the normal location stat of the related asset
                if (!prop.srcPosCollection) {
                    return getAssetPosition(locationStatCollection, relationshipForeignKey, time);
                }
                // we've followed the relationship and found a foreign key to look up the position for
                targetAssetId = relationshipForeignKey;
            } else {
                // no relationship to follow, so instead use ourselves as the source location lookup
                targetAssetId = assetId;
            }
            // find the source location given the collection and target
            const sourcePosition = getStatValue(
                prop.srcPosCollection,
                targetAssetId,
                prop.srcPosField,
                time,
            );
            // if we didn't have a value, but we were meant to, skip this propagation config
            if (!sourcePosition) {
                continue;
            }
            // now we turn our new position value into a cartesian3
            return convertPositionToCartesian3(sourcePosition, prop.srcPosFieldType);
        }

        // default behaviour, no propagation returned a location for this asset
        return getAssetPosition(locationStatCollection, assetId, time);
    };
}
